const defaultSelectors = {
  orbit: '[data-orbital-gradient-orbit]',
  first:
    '[data-orbital-gradient-orbit-wrap]:nth-child(1) [data-orbital-gradient-orbit-item]',
  second:
    '[data-orbital-gradient-orbit-wrap]:nth-child(2) [data-orbital-gradient-orbit-item]',
  images: '[data-orbital-gradient-motion] img',
};

const orbitalGradientInstances = new WeakMap();

const getMaskSupport = () => {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }

  return (
    CSS.supports('mask-image', 'url("data:image/png;base64,iVBORw0KGgo=")') ||
    CSS.supports(
      '-webkit-mask-image',
      'url("data:image/png;base64,iVBORw0KGgo=")'
    )
  );
};

const getReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const waitForImage = (image) => {
  if (image.complete) {
    return image.naturalWidth > 0
      ? Promise.resolve()
      : Promise.reject(new Error('Orbital gradient asset failed to load'));
  }

  return new Promise((resolve, reject) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener(
      'error',
      () => reject(new Error('Orbital gradient asset failed to load')),
      { once: true }
    );
  });
};

const waitForFirstFrame = () =>
  new Promise((resolve) => requestAnimationFrame(() => resolve()));

const loadMaskImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener(
      'error',
      () => reject(new Error('Orbital gradient mask failed to load')),
      { once: true }
    );
    image.src = src;
  });

export const canRunOrbitalGradient = ({
  width,
  reducedMotion,
  gsap,
  maskSupported,
}) =>
  width > 1024 &&
  !reducedMotion &&
  Boolean(
    gsap && typeof gsap.set === 'function' && typeof gsap.to === 'function'
  ) &&
  maskSupported;

export const getFooterGradientTriggerConfig = ({
  footer,
  clients,
  isEnhanced,
}) => ({
  trigger: isEnhanced && clients ? clients : footer,
  start: isEnhanced && clients ? 'top bottom' : null,
  endTrigger: isEnhanced && clients ? footer : null,
});

export const createOrbitalGradientController = async (
  root,
  {
    width = typeof window === 'undefined' ? 0 : window.innerWidth,
    reducedMotion = getReducedMotion(),
    gsap = globalThis.gsap,
    maskSupported = getMaskSupport(),
    nextFrame = waitForFirstFrame,
    loadMask = loadMaskImage,
    selectors = defaultSelectors,
    orbitSet = { xPercent: -50, yPercent: -50 },
  } = {}
) => {
  if (
    !root ||
    !canRunOrbitalGradient({ width, reducedMotion, gsap, maskSupported })
  ) {
    return null;
  }

  const orbit = root.querySelector(selectors.orbit);
  const first = root.querySelector(selectors.first);
  const second = root.querySelector(selectors.second);
  const images = [...root.querySelectorAll(selectors.images)];

  if (!orbit || !first || !second || images.length < 2) {
    return null;
  }

  try {
    const maskSrc = root.dataset?.orbitalGradientMaskSrc;
    await Promise.all([
      ...images.map(waitForImage),
      ...(maskSrc ? [loadMask(maskSrc)] : []),
    ]);
  } catch {
    return null;
  }

  gsap.set(orbit, {
    ...orbitSet,
    x: 0,
    y: 0,
    transformOrigin: 'center center',
  });

  const timelines = [
    gsap.to(orbit, {
      rotation: 360,
      ease: 'none',
      repeat: -1,
      duration: 30,
    }),
    gsap.to(first, {
      rotation: -360,
      ease: 'none',
      repeat: -1,
      duration: 30,
    }),
    gsap.to(second, {
      rotation: 360,
      ease: 'none',
      repeat: -1,
      duration: 40,
    }),
  ];
  let isInViewport = true;
  let isDocumentVisible =
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

  const syncPlayback = () => {
    const method = isInViewport && isDocumentVisible ? 'play' : 'pause';
    timelines.forEach((timeline) => timeline[method]());
  };

  const controller = {
    pause() {
      isInViewport = false;
      syncPlayback();
    },
    resume() {
      isInViewport = true;
      syncPlayback();
    },
    setDocumentVisible(isVisible) {
      isDocumentVisible = isVisible;
      syncPlayback();
    },
    destroy() {
      timelines.forEach((timeline) => timeline.kill());
    },
  };

  syncPlayback();
  await nextFrame();
  root.classList.add('is-orbital-gradient-ready');

  return controller;
};

const observeController = (root, controller) => {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }

  controller.pause();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target !== root) return;
      if (entry.isIntersecting) controller.resume();
      else controller.pause();
    });
  });
  observer.observe(root);
};

export const initOrbitalGradients = async (scope = document, options = {}) => {
  const roots = [...scope.querySelectorAll('[data-orbital-gradient]')];
  const observe = options.observe || observeController;
  const controllers = await Promise.all(
    roots.map(async (root) => {
      if (!orbitalGradientInstances.has(root)) {
        orbitalGradientInstances.set(
          root,
          createOrbitalGradientController(root, options)
        );
      }

      const controller = await orbitalGradientInstances.get(root);
      if (controller) observe(root, controller);
      return controller;
    })
  );
  const activeControllers = controllers.filter(Boolean);
  if (activeControllers.length && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState !== 'hidden';
      activeControllers.forEach((controller) =>
        controller.setDocumentVisible(isVisible)
      );
    });
  }

  return activeControllers;
};

if (typeof document !== 'undefined') {
  initOrbitalGradients();
}
